const DIY_EDITOR_CONTEXT_STORAGE_PREFIX = "dw.diyEditorContext.";
const DEFAULT_CONTEXT_MAX_AGE_MS = 1000 * 60 * 60 * 6;

function buildStorageKey(templateSlug) {
  const normalizedSlug = String(templateSlug || "").trim().toLowerCase();
  if (!normalizedSlug) {
    return "";
  }
  return `${DIY_EDITOR_CONTEXT_STORAGE_PREFIX}${normalizedSlug}`;
}

function safeGetItem(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // no-op
  }
}

function safeRemoveItem(storage, key) {
  try {
    storage.removeItem(key);
  } catch {
    // no-op
  }
}

function normalizeContextInput(context) {
  return {
    orderId: String(context?.orderId || "").trim(),
    orderCode: String(context?.orderCode || "").trim(),
    mode: String(context?.mode || "").trim().toLowerCase(),
    createdAt: Date.now(),
  };
}

function parseStoredContext(rawValue) {
  if (!rawValue) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    return {
      orderId: String(parsed.orderId || "").trim(),
      orderCode: String(parsed.orderCode || "").trim(),
      mode: String(parsed.mode || "").trim().toLowerCase(),
      createdAt: Number(parsed.createdAt || 0),
    };
  } catch {
    return null;
  }
}

function isValidContext(context) {
  if (!context) {
    return false;
  }
  if (!context.orderId) {
    return false;
  }
  if (context.mode !== "admin" && context.mode !== "revision" && context.mode !== "preview") {
    return false;
  }
  return true;
}

export function setDiyEditorContext(templateSlug, context) {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey = buildStorageKey(templateSlug);
  if (!storageKey) {
    return;
  }

  const normalizedContext = normalizeContextInput(context);
  if (!isValidContext(normalizedContext)) {
    safeRemoveItem(window.sessionStorage, storageKey);
    safeRemoveItem(window.localStorage, storageKey);
    return;
  }

  const serialized = JSON.stringify(normalizedContext);
  safeSetItem(window.sessionStorage, storageKey, serialized);
  safeSetItem(window.localStorage, storageKey, serialized);
}

export function clearDiyEditorContext(templateSlug) {
  if (typeof window === "undefined") {
    return;
  }
  const storageKey = buildStorageKey(templateSlug);
  if (!storageKey) {
    return;
  }
  safeRemoveItem(window.sessionStorage, storageKey);
  safeRemoveItem(window.localStorage, storageKey);
}

export function getDiyEditorContext(templateSlug, options = {}) {
  if (typeof window === "undefined") {
    return null;
  }
  const storageKey = buildStorageKey(templateSlug);
  if (!storageKey) {
    return null;
  }

  const rawSessionContext = safeGetItem(window.sessionStorage, storageKey);
  const rawLocalContext = safeGetItem(window.localStorage, storageKey);
  const parsedContext = parseStoredContext(rawSessionContext || rawLocalContext);
  if (!isValidContext(parsedContext)) {
    safeRemoveItem(window.sessionStorage, storageKey);
    safeRemoveItem(window.localStorage, storageKey);
    return null;
  }

  const maxAgeMs = Number(options.maxAgeMs || DEFAULT_CONTEXT_MAX_AGE_MS);
  if (Number.isFinite(maxAgeMs) && maxAgeMs > 0) {
    const ageMs = Date.now() - Number(parsedContext.createdAt || 0);
    if (!Number.isFinite(ageMs) || ageMs > maxAgeMs) {
      safeRemoveItem(window.sessionStorage, storageKey);
      safeRemoveItem(window.localStorage, storageKey);
      return null;
    }
  }

  return parsedContext;
}
